import { v4 as uuidv4 } from 'uuid';
import { logger } from '@cutwise/utils';

import { IS_TEST_ENVIRONMENT } from '@/common/constants/isTestEnvironment';
import { HttpStatus } from '@/common/network/constants/httpStatus';
import DetectNetbox from '@/common/network/middleware/DetectNetbox';
import { netboxifyURL } from '@/netbox/helpers/netbox';

import Notifier from '../services/Notifier';
import AppError, { DESCRIPTION_DEFAULT, TITLE_DEFAULT } from '../state/AppError';

import Auth from './middleware/Auth';
import ContentType from './middleware/ContentType';
import HandleErrors from './middleware/HandleErrors';
import SecurityCheck from './middleware/SecurityCheck';
import Query from './Query';
import QueryBuilder, { HttpMethod } from './QueryBuilder';
import { queryMockResolver } from './QueryMockResolver';
import QueryResult from './QueryResult';
import URLBuilder from './URLBuilder';

export const NETBOX_KEY = 'netbox';
export const NETBOX_HOSTNAME = 'netbox-hostname';
export const METHOD_GET: HttpMethod = 'GET';
export const METHOD_POST: HttpMethod = 'POST';
export const METHOD_PUT: HttpMethod = 'PUT';
export const METHOD_PATCH: HttpMethod = 'PATCH';
export const METHOD_DELETE: HttpMethod = 'DELETE';
export const METHOD_HEAD: HttpMethod = 'HEAD';
export const METHOD_COPY: HttpMethod = 'COPY';
export const METHOD_LINK: HttpMethod = 'LINK';
export const METHOD_UNLINK: HttpMethod = 'UNLINK';

export const HEADER_X_TOTAL_COUNT = 'X-Total-Count';
export const HEADER_X_PRODUCTS_COUNT = 'X-Products-Count';
export const HEADER_X_TOTAL_CASH_EXPENSES = 'X-Total-Cash-Expenses';
export const HEADER_X_TOTAL_CASH_ENROLLMENTS = 'X-Total-Cash-Enrollments';
export const HEADER_X_TOTAL_BONUSES_EXPENSES = 'X-Total-Bonuses-Expenses';
export const HEADER_X_TOTAL_BONUSES_ENROLLMENTS = 'X-Total-Bonuses-Enrollments';
export const HEADER_X_EXPENSES = 'X-Expenses';
export const HEADER_X_NETBOX = 'X-NetBox';
export const HEADER_X_NETBOX_HOSTNAME = 'X-NetBox-Hostname';

function prepareQueryContent(data: unknown) {
  if (data instanceof Blob) {
    return data;
  }

  let result = JSON.parse(JSON.stringify(data));

  // for "null", "0", "''", "undefined"
  if (!result) {
    return result;
  }

  if (typeof result === 'string') {
    return netboxifyURL(result);
  }

  if (Array.isArray(data)) {
    result = data.map((item) => prepareQueryContent(item));
  } else {
    const keys = Object.keys(result);

    if (keys.length > 0) {
      keys.forEach((key) => {
        result[key] = prepareQueryContent(result[key]);
      });
    }
  }

  return result;
}

export default class CutwiseAPIClient {
  static createQueryBuilder() {
    return new QueryBuilder();
  }

  // TODO need to rewrite many things to proper TS typing and then remove Any
  static sendQuery<T = Any>(query: Query): Promise<QueryResult<T>> {
    const mockedResult = queryMockResolver.resolve<T>(query);

    if (mockedResult) {
      return Promise.resolve(mockedResult);
    }

    query.addRequestMiddleware(SecurityCheck);
    query.addRequestMiddleware(ContentType);

    if (!query.isAnonymous) {
      query.addRequestMiddleware(Auth);
    }

    query.addResponseMiddleware(HandleErrors(query.suppressErrorNotifications));
    query.addResponseMiddleware(DetectNetbox);

    return new Promise<QueryResult<T>>((resolve, reject) => {
      query.requestMiddlewares.forEach((middleware) => middleware(query));

      const notificationId = uuidv4();

      const requestParams: RequestInit = {
        method: query.method ?? undefined,
        cache: 'no-cache',
        body: query.requestBody ? (query.serializer.serialize(query.requestBody) ?? undefined) : undefined,
        headers: query.headers,
        signal: query.abortSignal ?? undefined,
      };

      const url = query.base
        ? URLBuilder.build(query.base, query.path, query.queryStringParams)
        : URLBuilder.buildUrl(query.path, query.queryStringParams);

      function makeRequest(resolveFetch: (res: Response) => void): Promise<void> {
        return fetch(url, requestParams)
          .then((res) => {
            Notifier.dismissNotification(notificationId);

            resolveFetch(res);
          })
          .catch((err) => {
            if (!window.navigator.onLine) {
              window.addEventListener('online', () => makeRequest(resolveFetch));
            } else {
              throw err;
            }
          });
      }

      const requestPromise = new Promise<Response>((resolveFetch) => {
        let requestTimeout: SetTimeout | undefined;
        let retryTimeout: SetTimeout | undefined;

        function requestWrapperFunction() {
          makeRequest(resolveFetch).catch((err: Error) => {
            if (!query.repeatRequestTimeout) {
              clearTimeout(requestTimeout);
              clearTimeout(retryTimeout);

              reject(new AppError(null, err.message, DESCRIPTION_DEFAULT));

              return;
            }

            retryTimeout = setTimeout(() => {
              requestWrapperFunction();
            }, query.repeatRequestRetryTimeout);

            // Be sure that timeout for request initialized only once
            if (!requestTimeout) {
              Notifier.warn('Internet connection problems. Trying to connect.', {
                delay: query.repeatRequestTimeout,
                toastId: notificationId,
              });

              requestTimeout = setTimeout(() => {
                clearTimeout(requestTimeout);
                clearTimeout(retryTimeout);

                reject(new AppError(null, TITLE_DEFAULT, DESCRIPTION_DEFAULT));
              }, query.repeatRequestTimeout);
            }
          });
        }

        requestWrapperFunction();
      });

      requestPromise.then((res) => {
        let decodingPromise: Promise<unknown>;

        switch (query.responseType) {
          case 'blob-or-json':
            // This condition for edge cases, when we want to download file, but can receive JSON in response
            // specifically for this task https://octonus-teams.com/jira/browse/CUT-7354
            if (res.headers.get('content-type') === 'application/json') {
              decodingPromise = res.json();
            } else {
              decodingPromise = res.blob();
            }

            break;
          case 'text':
            decodingPromise = res.text();

            break;
          case 'blob':
            decodingPromise = res.blob();

            break;
          case 'json':
          default:
            decodingPromise = res.json();

            break;
        }

        decodingPromise
          .catch((e) => {
            // empty response body (and subsequent parsing error) is ok behaviour for NoContent status
            // and does not require logging
            if (!IS_TEST_ENVIRONMENT && ![HttpStatus.NoContent, HttpStatus.Unauthorized].includes(res?.status)) {
              logger.error(res?.url, e);
            }

            return null;
          })
          .then((json) => {
            const queryResult = new QueryResult<T>();

            queryResult.networkResponse = res;
            queryResult.content = prepareQueryContent(json) as T;

            query.result = queryResult;

            // reverse is really needed here because we have to call default middlewares first
            query.responseMiddlewares.reverse().forEach((middleware) => middleware(query));

            resolve(queryResult);
          })
          .catch(reject); // pass error to outer promise
      });
    });
  }
}
